import { FC, KeyboardEvent, ReactNode } from 'react';
import {
  BodyText,
  Card as DSCard,
  CardProps as DSCardProps,
} from '@servicetitan/design-system';
import { Card as AnvilCard, Flex } from '@servicetitan/anvil2';
import classNames from 'classnames';
import { formatDateTimeShort } from '@lib/utils/formatDate';
import { JobIdLookupAppearance } from './appearance';
import { JobResult } from './useJobLookup';
import styles from './JobIdLookup.module.css';

export interface JobCardProps extends Omit<DSCardProps, 'onClick'> {
  /** The resolved tenant job to preview. Pass `null` to render nothing. */
  job: JobResult | null;
  /**
   * Optional extras rendered below the job preview (e.g. a "Change Job"
   * button in the read-mode card).
   */
  children?: ReactNode;
  /**
   * Visual variant. Defaults to `classic` (design-system Card) so existing
   * callers stay unchanged; pass `anvil2` to render an Anvil2 Card whose
   * border tokens match the Anvil2 TextField in the same form.
   */
  appearance?: JobIdLookupAppearance;
  /**
   * Activation callback for the candidate (pre-select) card. Fires on click
   * and on keyboard activation (Enter / Space) when the card is focused. The
   * event object isn't forwarded because the only consumer
   * (`JobIdLookup#onSelectJob`) reads from lookup state, not from the event.
   */
  onClick?: () => void;
}

/**
 * At-a-glance preview for a resolved job: job number, created-on date,
 * customer name and job-site address. Returns nothing when `job` is null so
 * callers can render unconditionally.
 */
export const JobCard: FC<JobCardProps> = ({
  job,
  children,
  appearance = 'classic',
  className,
  onClick,
  id,
  ...cardProps
}) => {
  if (!job) return null;

  const body = (
    <>
      <BodyText>
        <BodyText size='small'>
          Job # {job.id} - Created on{' '}
          {formatDateTimeShort(new Date(job.createdAt))}
        </BodyText>
        <BodyText size='xsmall' subdued>
          {job.customer.name} - {job.jobLocation.address}
        </BodyText>
      </BodyText>
      {children}
    </>
  );

  if (appearance === 'anvil2') {
    const interactiveProps = onClick
      ? {
          role: 'button' as const,
          tabIndex: 0,
          // Mirror the click handler for keyboard activation. Enter and Space
          // are the standard activation keys for role='button'; pressing them
          // confirms the candidate job the same way clicking the card does.
          onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              onClick();
            }
          },
        }
      : null;

    return (
      <AnvilCard
        id={id}
        padding='small'
        onClick={onClick}
        className={classNames(styles.anvil2Card, className, {
          [styles.anvil2CardInteractive]: !!onClick,
        })}
        {...interactiveProps}
      >
        <Flex direction='column' gap='2'>
          {body}
        </Flex>
      </AnvilCard>
    );
  }

  return (
    <DSCard {...cardProps} id={id} className={className} onClick={onClick} thin>
      {body}
    </DSCard>
  );
};
